import { type ThemeOptions } from '@mui/material';

import LogoCollapsed from '../static/images/ayrade-brand-mark.svg';
import LogoText from '../static/images/ayrade-wordmark-light.svg';
import { hexToRGB } from '../utils/Colors';
import { fileUri } from '../utils/Environment';
import applianceComponents from './ApplianceDesign';
import { FONT_FAMILY_CODE, type LabelColor, LabelColorDict } from './Theme';

const EE_COLOR = '#0f766e';

export const THEME_LIGHT_DEFAULT_BACKGROUND = '#fafdff';
const THEME_LIGHT_DEFAULT_PRIMARY = '#0039A9';
const THEME_LIGHT_DEFAULT_SECONDARY = '#66B2FF';
const THEME_LIGHT_DEFAULT_ACCENT = '#E8F3FF';
const THEME_LIGHT_DEFAULT_PAPER = '#ffffff';
const THEME_LIGHT_DEFAULT_NAV = '#ffffff';

const ThemeLight = (
  logo: string | null = null,
  logo_collapsed: string | null = null,
  background: string | null = null,
  paper: string | null = null,
  nav: string | null = null,
  primary: string | null = null,
  secondary: string | null = null,
  accent: string | null = null,
  text_color = 'rgba(0, 0, 0, 0.87)',
): ThemeOptions => ({
  logo: logo || fileUri(LogoText),
  logo_collapsed: logo_collapsed || fileUri(LogoCollapsed),
  borderRadius: 16,
  shape: { borderRadius: 16 },
  palette: {
    mode: 'light',
    common: {
      white: '#ffffff',
      black: '#000000',
      grey: '#667085',
      lightGrey: 'rgba(16, 24, 40, 0.62)',
    },
    text: {
      primary: text_color,
      secondary: 'rgba(16, 24, 40, 0.68)',
      disabled: 'rgba(16, 24, 40, 0.42)',
    },
    divider: 'rgba(16, 24, 40, 0.1)',
    action: {
      hover: hexToRGB(primary || THEME_LIGHT_DEFAULT_PRIMARY, 0.07),
      selected: hexToRGB(primary || THEME_LIGHT_DEFAULT_PRIMARY, 0.12),
      disabledBackground: 'rgba(16, 24, 40, 0.06)',
    },
    error: {
      main: '#f44336',
      dark: '#c62828',
    },
    warn: { main: '#ffa726' },
    dangerZone: {
      main: '#f6685e',
      light: '#fbc2be',
      dark: '#d1584f',
      contrastText: '#000000',
    },
    success: { main: '#1f9d55' },
    warning: { main: '#ed6c02' },
    primary: { main: primary || THEME_LIGHT_DEFAULT_PRIMARY },
    secondary: { main: secondary || THEME_LIGHT_DEFAULT_SECONDARY },
    gradient: { main: '#7dc2ff' },
    border: {
      lightBackground: hexToRGB('#0f172a', 0.12),
      primary: hexToRGB(primary || THEME_LIGHT_DEFAULT_PRIMARY, 0.3),
      secondary: hexToRGB(secondary || THEME_LIGHT_DEFAULT_SECONDARY, 0.3),
      pagination: hexToRGB('#14325c', 0.38),
      paper: hexToRGB('#0f172a', 0.1),
    },
    pagination: { main: '#1f3b6b' },
    chip: { main: '#183153' },
    labelChipMap: new Map<string, LabelColor>([
      [
        LabelColorDict.Red, {
          backgroundColor: 'rgba(220, 38, 38, 0.08)',
          color: '#dc2626',
        }], [
        LabelColorDict.Green, {
          backgroundColor: 'rgba(22, 163, 74, 0.1)',
          color: '#15803d',
        }], [
        LabelColorDict.Orange, {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          color: '#d97706',
        }],
    ]),
    ai: {
      main: '#5b7cff',
      light: '#90b4ff',
      dark: '#2d5eff',
      contrastText: '#ffffff',
    },
    ee: {
      main: EE_COLOR,
      background: hexToRGB(EE_COLOR, 0.2),
      lightBackground: hexToRGB(EE_COLOR, 0.08),
      contrastText: '#ffffff',
    },
    xtmhub: { main: '#69b7ff' },
    widgets: {
      securityDomains: {
        colors: {
          success: 'rgb(2,129,8)',
          intermediate: 'rgb(255 216 0)',
          warning: 'rgb(245, 166, 35)',
          failed: 'rgb(220, 81, 72)',
          pending: 'rgba(248,243,243,0.37)',
          unknown: 'rgba(73,72,72,0.37)',
        },
      },
    },
    background: {
      default: background || THEME_LIGHT_DEFAULT_BACKGROUND,
      paper: paper || THEME_LIGHT_DEFAULT_PAPER,
      nav: nav || THEME_LIGHT_DEFAULT_NAV,
      accent: accent || THEME_LIGHT_DEFAULT_ACCENT,
      shadow: 'rgba(15, 23, 42, 0.12)',
      code: accent || THEME_LIGHT_DEFAULT_ACCENT,
      paperInCard: '#f8fafc',
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    body2: {
      fontSize: '0.8rem',
      lineHeight: '1.2rem',
      color: text_color,
    },
    body1: {
      fontSize: '0.9rem',
      color: text_color,
    },
    overline: {
      fontWeight: 500,
      color: text_color,
    },
    h1: {
      margin: '0 0 10px 0',
      padding: 0,
      fontWeight: 600,
      fontSize: 24,
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
      color: text_color,
    },
    h2: {
      margin: '0 0 10px 0',
      padding: 0,
      fontWeight: 600,
      fontSize: 16,
      textTransform: 'none',
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
      color: text_color,
    },
    h3: {
      margin: '0 0 10px 0',
      padding: 0,
      color: text_color,
      fontWeight: 400,
      fontSize: 13,
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    },
    h4: {
      height: 15,
      margin: '0 0 10px 0',
      padding: 0,
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 500,
      color: text_color,
    },
    h5: {
      fontWeight: 400,
      fontSize: 13,
      textTransform: 'none',
      marginTop: -4,
      color: text_color,
    },
    h6: {
      fontWeight: 400,
      fontSize: 18,
      color: text_color,
      fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 18,
      color: text_color,
    },
  },
  components: {
    MuiFormControl: {
      defaultProps: { variant: 'standard' },
      styleOverrides: { root: { color: text_color } },
    },
    MuiTextField: {
      defaultProps: { variant: 'standard' },
      styleOverrides: { root: { color: text_color } },
    },
    MuiSelect: {
      defaultProps: { variant: 'standard' },
      styleOverrides: { root: { color: text_color } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(16, 24, 40, 0.08)',
          boxShadow: '0 8px 28px rgba(15, 23, 42, 0.06)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollbarColor: `${accent || THEME_LIGHT_DEFAULT_ACCENT} ${paper || THEME_LIGHT_DEFAULT_PAPER}`,
          scrollbarWidth: 'thin',
        },
        body: {
          'scrollbarColor': `${accent || THEME_LIGHT_DEFAULT_ACCENT} ${nav || THEME_LIGHT_DEFAULT_NAV}`,
          'scrollbarWidth': 'thin',
          'background':
            `linear-gradient(180deg, ${hexToRGB(primary || THEME_LIGHT_DEFAULT_PRIMARY, 0.06)} 0, transparent 320px), ${background || THEME_LIGHT_DEFAULT_BACKGROUND}`,
          'html': { WebkitFontSmoothing: 'auto' },
          'a': { color: primary || THEME_LIGHT_DEFAULT_PRIMARY },
          'input:-webkit-autofill': {
            WebkitAnimation: 'autofill 0s forwards',
            animation: 'autofill 0s forwards',
            WebkitTextFillColor: '#000000 !important',
            caretColor: 'transparent !important',
            WebkitBoxShadow:
              '0 0 0 1000px rgba(240, 247, 255, 1) inset !important',
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          },
          'pre': {
            fontFamily: FONT_FAMILY_CODE,
            color: `${text_color} !important`,
            background: `${accent || THEME_LIGHT_DEFAULT_ACCENT} !important`,
            borderRadius: 4,
          },
          'pre.light': {
            fontFamily: FONT_FAMILY_CODE,
            background: `${nav || THEME_LIGHT_DEFAULT_NAV} !important`,
            borderRadius: 4,
          },
          'code': {
            fontFamily: FONT_FAMILY_CODE,
            color: `${text_color} !important`,
            background: `${accent || THEME_LIGHT_DEFAULT_ACCENT} !important`,
            padding: 3,
            fontSize: 12,
            fontWeight: 400,
            borderRadius: 4,
          },
          '.w-md-editor': {
            'boxShadow': 'none',
            'background': 'transparent',
            'borderBottom': '1px solid rgba(20, 50, 92, 0.42) !important',
            'transition': 'borderBottom .3s',
            '&:hover': { borderBottom: '2px solid rgba(20, 50, 92, 0.72) !important' },
            '&:focus-within': { borderBottom: `2px solid ${primary || THEME_LIGHT_DEFAULT_PRIMARY} !important` },
          },
          '.error .w-md-editor': {
            'border': '0 !important',
            'borderBottom': '2px solid #f44336 !important',
            '&:hover': {
              border: '0 !important',
              borderBottom: '2px solid #f44336 !important',
            },
            '&:focus': {
              border: '0 !important',
              borderBottom: '2px solid #f44336 !important',
            },
          },
          '.w-md-editor-toolbar': {
            border: '0 !important',
            backgroundColor: 'transparent !important',
            color: `${text_color} !important`,
          },
          '.w-md-editor-toolbar li button': { color: `${text_color} !important` },
          '.w-md-editor-text textarea': {
            fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
            fontSize: 13,
            color: text_color,
          },
          '.w-md-editor-preview': { boxShadow: 'inset 1px 0 0 0 rgba(20, 50, 92, 0.18)' },
          '.wmde-markdown': {
            background: 'transparent',
            fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
            fontSize: 13,
            color: text_color,
          },
          '.wmde-markdown tr': { background: 'transparent !important' },
          '.react-grid-placeholder': { backgroundColor: `${accent || THEME_LIGHT_DEFAULT_ACCENT} !important` },
          '.react_time_range__track': {
            backgroundColor: hexToRGB(primary || THEME_LIGHT_DEFAULT_PRIMARY, 0.12) + ' !important',
            borderLeft: `1px solid ${primary || THEME_LIGHT_DEFAULT_PRIMARY} !important`,
            borderRight: `1px solid ${primary || THEME_LIGHT_DEFAULT_PRIMARY} !important`,
          },
          '.react_time_range__handle_marker': { backgroundColor: `${primary || THEME_LIGHT_DEFAULT_PRIMARY} !important` },
          '.leaflet-container': { backgroundColor: `${paper || THEME_LIGHT_DEFAULT_PAPER} !important` },
          '.react-grid-item .react-resizable-handle::after': {
            borderRight: '2px solid rgba(20, 50, 92, 0.45) !important',
            borderBottom: '2px solid rgba(20, 50, 92, 0.45) !important',
          },
        },
      },
    },
    MuiInputBase: { styleOverrides: { root: { color: text_color } } },
    ...applianceComponents('light', primary || THEME_LIGHT_DEFAULT_PRIMARY, paper || THEME_LIGHT_DEFAULT_PAPER),
  },
});

export default ThemeLight;
